import React, { useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import useSendInfo from 'components/MessageBuilder/useSendInfo';
import InputControl from 'components/Inputs/InputControl';
import { gutters } from 'app/spacing';
import { css } from '@emotion/core';
import { fontSizes, fontWeights } from 'app/typography';
import Button from 'components/Button';
import { useModalControl } from 'hooks/useModalControl';
import { MessageBuilder, useDefaultTemplate } from 'components/MessageBuilder';
import MessageTemplatePicker from 'components/MessageTemplatePicker';
import LibraryService from 'services/LibraryService';
import { toastVariant, useToast } from 'components/ToastProvider';
import { useEmailFromDomainEnabledEntitlement } from 'hooks/useEmailFromDomainEnabledEntitlement';
import { SENDER_TYPE } from 'components/MessageBuilder/components';
import { useIntegratedInbox } from 'hooks/useIntegratedInbox';
import { AiExpandableSinglePrompt } from 'ts-components/AiExpandableSinglePrompt';
import {
  FLOW_TYPE,
  TASK_TYPE,
  FLOW_STATUS,
  GUARD_RAIL_CODES,
} from 'ts-components/Conversation';
import { useAIFeatures, AI_FEATURE_CONFIG_KEYS } from 'hooks/useAIFeatures';
import useCan from 'hooks/useCan';

const DELAY = 3000;

export const MESSAGE_TYPES = {
  broadcasts: 'broadcasts',
  automations: 'automations',
  templates: 'templates',
};

export const BROADCAST_TYPES = {
  single_send: 'single_send',
  scheduled_broadcasts: 'scheduled_broadcasts',
};

const EmailBlock = styled.div`
  display: block;
`;

const StyledTextButton = styled(Button)`
  height: ${fontSizes.small};
  margin: 0;
  margin-right: ${gutters.spacing(4)}px;
  padding: 0;
  margin-bottom: ${gutters.spacing(0, -4)}px; //Design nude
`;

const Bolded = styled.span`
  font-size: inherit;
  font-weight: ${fontWeights.bold};
`;

const StyledButton = styled(Button)`
  margin-bottom: 0;
  margin-left: 0;
  margin-right: ${gutters.spacing(4)}px;
`;

const StyledInputControl = styled(InputControl)`
  margin-bottom: 0;

  && label {
    margin-top: ${gutters.spacing(2, 2)}px;
    margin-bottom: ${gutters.spacing(4, 4)}px;
  }

  ${({ isEmailSelected }) =>
    isEmailSelected
      ? css`
          && label {
            margin-top: 0;
            margin-bottom: ${gutters.spacing(3, 1)}px;
          }
        `
      : css`
          && label {
            margin-top: 0;
            margin-bottom: ${gutters.spacing(2, 2)}px;
          }
        `}
  ${({ isLastElement, canEdit }) =>
    isLastElement &&
    css`
      && label {
        margin-top: 0;
        margin-bottom: ${canEdit ? gutters.spacing(3, 1) : 0}px;
      }
    `}
`;

export const EmailPicker = React.forwardRef(
  (
    {
      entityId,
      enableAllObjectMergeFields,
      automation,
      email,
      onChange,
      readonlySenderInfo = false,
      mergeFields = null,
      loadingMergeFields = false,
      customObject = null,
      disabled = false,
      useAllIntgegratedInboxes = false,
      emailType = MESSAGE_TYPES.broadcasts,
      broadcastType = BROADCAST_TYPES.single_send,
      expandAiInPlace = false,
    },
    ref
  ) => {
    const emailFromDomainEnabled = useEmailFromDomainEnabledEntitlement();
    const { aiFeaturesEnabled } = useAIFeatures(
      AI_FEATURE_CONFIG_KEYS.email_builder
    );
    const [backToPicker, setBackToPicker] = useState(false);
    const canViewLibrary = useCan({
      view: 'sections',
      for: 'library_section',
    });

    const { t } = useTranslation();
    const [showToast] = useToast();
    const [modalLayer, setModalLayer] = useState(1);
    const [modalOpen, { showModal, hideModal }] = useModalControl();
    const [
      messagePickerOpen,
      { showModal: showMessagePicker, hideModal: hideMessagePicker },
    ] = useModalControl();

    const { accounts } = useIntegratedInbox(useAllIntgegratedInboxes);
    const { sendFromString, fromNameString } = useSendInfo(
      email,
      emailFromDomainEnabled,
      accounts
    );

    const clearEmail = useCallback(() => {
      onChange(null);
    }, [onChange]);

    const handleCreateNewEmail = useCallback(() => {
      clearEmail();
      setModalLayer(1);
      showModal();
    }, [showModal, clearEmail]);

    const handleMessagePickerOpen = useCallback(() => {
      setModalLayer(2);
      showMessagePicker();
    }, [showMessagePicker]);

    const isEmailSelected = !!email?.craftJsContent;
    const craftJsContent = useDefaultTemplate(email?.craftJsContent);
    const initialData = useMemo(
      () => ({ ...email, craftJsContent }),
      [craftJsContent, email]
    );

    const handleBuilderConfirm = useCallback(
      async (messageData, shouldCloseBuilder) => {
        await onChange(messageData);
        setBackToPicker(false);
        showToast({
          message: t('Message Template was successfully saved'),
          variant: toastVariant.SUCCESS,
        });
        if (shouldCloseBuilder) {
          hideMessagePicker();
          setModalLayer(1);
          hideModal();
        }
      },
      [onChange, hideMessagePicker, hideModal, showToast, t]
    );

    const handleBuilderCancel = useCallback(() => {
      if (backToPicker) {
        onChange(null);
        setBackToPicker(false);
        showMessagePicker();
      } else {
        setModalLayer(1);
        hideMessagePicker();
      }
      hideModal();
    }, [
      backToPicker,
      hideModal,
      showMessagePicker,
      hideMessagePicker,
      onChange,
    ]);

    const handleMessagePickerConfirm = useCallback(
      async (message) => {
        try {
          setBackToPicker(true);
          const messageData = await LibraryService.getTemplateByTypeId(message);
          onChange(
            emailFromDomainEnabled
              ? messageData
              : {
                  ...messageData,
                  ...(messageData.senderType !== SENDER_TYPE.BUSINESS && {
                    sender: {
                      custtomName: '',
                      id: null,
                      type: SENDER_TYPE.SPECIFIC_INTEGRATED_INBOX,
                      senderType: SENDER_TYPE.SPECIFIC_INTEGRATED_INBOX,
                    },
                  }),
                  senderField: null,
                  senderRole: null,
                  senderTeamMember: null,
                }
          );
          showModal();
        } catch {
          showToast({
            message: t(
              'Failed to load chosen message. Please try again or contact Kizen support.'
            ),
            variant: toastVariant.FAILURE,
            delay: DELAY,
          });
        }
      },
      [onChange, showModal, showToast, t, emailFromDomainEnabled]
    );

    const extractEmailGenerationResult = useCallback((response) => {
      const runMetadata = response.run_metadata;
      const guardrailCode = runMetadata?.email_templates?.guardrail_code;
      const guardrailMessage = runMetadata?.email_templates?.guardrail_message;
      const sessionId = response.session_id || runMetadata?.session_id;
      const generatedEmail =
        Object.keys(runMetadata?.email_templates || {}).length > 0
          ? Object.values(runMetadata?.email_templates)?.[0]
          : null;

      return {
        hasResult: !!generatedEmail,
        sessionId,
        guardrailCode,
        guardrailMessage,
      };
    }, []);

    const handleAiEmailGeneration = useCallback(
      async (emailFlowStatusData) => {
        // Handle next_actions URL pattern (chat session)
        const navigateUrl = emailFlowStatusData?.next_actions?.[0]?.url;
        if (navigateUrl) {
          try {
            const guardrailCode =
              emailFlowStatusData.run_metadata?.guardrail_code;
            const guardrailMessage =
              emailFlowStatusData.run_metadata?.guardrail_message;
            const url = new URL(navigateUrl);
            const pathParts = url.pathname.split('/');
            const emailId = pathParts[pathParts.length - 2];

            const messageData = await LibraryService.getTemplateByTypeId({
              type: emailType,
              id: emailId,
            });
            if (guardrailCode && guardrailMessage) {
              showToast({
                message: guardrailMessage,
                variant:
                  guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
                    ? toastVariant.ALERT
                    : toastVariant.FAILURE,
                autohide: false,
              });
            } else {
              showToast({
                message: t('Email created successfully in Kizen AI Lab'),
                variant: toastVariant.SUCCESS,
              });
            }
            setTimeout(() => {
              onChange(
                emailFromDomainEnabled
                  ? messageData
                  : {
                      ...messageData,
                      ...(messageData.senderType !== SENDER_TYPE.BUSINESS && {
                        sender: {
                          customName: '',
                          id: null,
                          type: SENDER_TYPE.SPECIFIC_INTEGRATED_INBOX,
                          senderType: SENDER_TYPE.SPECIFIC_INTEGRATED_INBOX,
                        },
                      }),
                      senderField: null,
                      senderRole: null,
                      senderTeamMember: null,
                    }
              );
              showModal();
            }, 1500);
            return;
          } catch (error) {
            showToast({
              message: t('Failed to load generated email. Please try again.'),
              variant: toastVariant.FAILURE,
              delay: DELAY,
            });
            return;
          }
        }

        // Handle run_metadata pattern (one-shot)
        const runMetadata = emailFlowStatusData.run_metadata || {};
        const generatedEmailTemplateURl = runMetadata?.next_actions?.[0]?.url;
        const generatedEmailTemplate = runMetadata?.next_actions?.[0];
        const url = new URL(generatedEmailTemplateURl);
        const pathParts = url.pathname.split('/');
        const emailId = pathParts[pathParts.length - 2];

        if (!emailId || generatedEmailTemplate.status === FLOW_STATUS.FAILED) {
          showToast({
            message: t('Error generating email please try again later'),
            variant: toastVariant.FAILURE,
            delay: DELAY,
          });
          return;
        }

        const guardrailCode = runMetadata?.email_templates?.guardrail_code;
        const guardrailMessage =
          runMetadata?.email_templates?.guardrail_message;

        if (guardrailCode && guardrailMessage) {
          showToast({
            message: guardrailMessage,
            variant:
              guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
                ? toastVariant.ALERT
                : toastVariant.FAILURE,
            autohide: false,
          });
        } else {
          showToast({
            message: t('Email created successfully in Kizen AI Lab'),
            variant: toastVariant.SUCCESS,
          });
        }

        setTimeout(() => {
          handleMessagePickerConfirm({
            type: emailType,
            id: emailId,
          });
        }, 1500);
      },
      [
        showToast,
        t,
        handleMessagePickerConfirm,
        emailType,
        emailFromDomainEnabled,
        onChange,
        showModal,
      ]
    );

    const handleEmailGenerationFailure = useCallback(
      (error) => {
        const usageError = error?.response?.data?.run_context?.usage;
        if (usageError && usageError[0]) {
          showToast({
            message: usageError[0],
            variant: toastVariant.FAILURE,
          });
        } else {
          showToast({
            message: t('Email Generation Failed, Please try again later'),
            variant: toastVariant.FAILURE,
          });
        }
      },
      [showToast, t]
    );
    return (
      <>
        <div ref={ref}>
          {disabled ? null : (
            <>
              {modalOpen && initialData?.craftJsContent ? (
                <MessageBuilder
                  enableAllObjectMergeFields={enableAllObjectMergeFields}
                  initialData={initialData}
                  modalLayer={modalLayer}
                  readonlySenderInfo={readonlySenderInfo}
                  onConfirm={handleBuilderConfirm}
                  onCancel={handleBuilderCancel}
                  mergeFields={mergeFields}
                  loadingMergeFields={loadingMergeFields}
                  customObject={customObject}
                  useAllIntgegratedInboxes={useAllIntgegratedInboxes}
                  customObjectName={customObject?.name}
                />
              ) : null}
              <MessageTemplatePicker
                show={messagePickerOpen}
                onCancel={hideMessagePicker}
                onSubmit={handleMessagePickerConfirm}
              />
            </>
          )}
          {isEmailSelected && (
            <StyledInputControl
              isEmailSelected={isEmailSelected}
              variant="outline"
              label={t('Selected Email')}
            />
          )}
          {isEmailSelected && sendFromString && (
            <StyledInputControl
              variant="outline"
              isEmailSelected
              label={
                <>
                  <Bolded>{t('Send From')}:</Bolded> {sendFromString}
                </>
              }
            />
          )}
          {isEmailSelected && fromNameString && (
            <StyledInputControl
              variant="outline"
              isEmailSelected
              label={
                <>
                  <Bolded>{t('From Name')}:</Bolded> {fromNameString}
                </>
              }
            />
          )}
          {!isEmailSelected && !disabled ? (
            <StyledInputControl
              isEmailSelected={isEmailSelected}
              variant="outline"
              label={t('Choose Email To Send')}
            />
          ) : null}

          <EmailBlock>
            {isEmailSelected ? (
              <StyledInputControl
                margin={1}
                variant="outline"
                isLastElement={isEmailSelected}
                canEdit={!disabled}
                label={
                  <>
                    <Bolded>{t('Selected')}:</Bolded> {email.subject}
                  </>
                }
              >
                {disabled ? null : (
                  <div>
                    <StyledTextButton
                      variant="text"
                      color="blue"
                      onClick={showModal}
                    >
                      {t('View/Edit Email ')}
                    </StyledTextButton>
                    <StyledTextButton
                      variant="text"
                      color="red"
                      onClick={clearEmail}
                    >
                      {t('Remove Email ')}
                    </StyledTextButton>
                  </div>
                )}
              </StyledInputControl>
            ) : (
              <>
                {canViewLibrary ? (
                  <StyledButton
                    variant={'outline'}
                    color={'blue'}
                    onClick={handleMessagePickerOpen}
                    data-qa="email-choose"
                    disabled={disabled}
                  >
                    {t('Choose Email From Library')}
                  </StyledButton>
                ) : null}
                <StyledButton
                  variant={'outline'}
                  color={'green'}
                  onClick={handleCreateNewEmail}
                  data-qa="email-create"
                  disabled={disabled}
                >
                  {t('Create New Email')}
                </StyledButton>
                {aiFeaturesEnabled ? (
                  <AiExpandableSinglePrompt
                    flowType={FLOW_TYPE.email_builder}
                    taskType={TASK_TYPE.EMAIL_PLAN_GENERATION}
                    context={{
                      ...(automation
                        ? {
                            automation_id: automation.id,
                            broadcast: broadcastType,
                          }
                        : { broadcast: broadcastType }),
                    }}
                    importResults={true}
                    onOneShotSuccess={handleAiEmailGeneration}
                    onChatSessionSuccess={handleAiEmailGeneration}
                    onGenerationFailure={handleEmailGenerationFailure}
                    extractGenerationResult={extractEmailGenerationResult}
                    entityName={t('Email')}
                    messagesConfig={{
                      placeholder: t('Describe your email'),
                      buildingMessage: t('Building your email'),
                      successMessage: t('Email Generated Successfully'),
                      failureMessage: t('Email Generation Failed'),
                      expandedPlaceholder: t('Add additional context'),
                      closingMessage: t('Closing session'),
                      initialMessage: t(
                        'What type of email would you like to create?'
                      ),
                    }}
                    inModal
                    expandInPlace={expandAiInPlace}
                    popoverProps={{
                      placement: 'bottom-start',
                    }}
                  />
                ) : null}
              </>
            )}
          </EmailBlock>
        </div>
      </>
    );
  }
);
